import { Platform as NativePlatform } from '@hyperconnect/azar-jsapi';

import {
  DTOGemProductModule,
  DTOItemProductModule,
  DTOVividiOneTimePlanModule,
} from 'src/apis/dto/DTOShopModule';
import { ShopOrigin } from 'src/stores/shop/types';

import { ProductModule } from './gemLack';
import { Platform } from './Platform';

export const enum PgPaymentMethod {
  ADYEN = 'ADYEN',
  TOSS = 'TOSS',
  STRIPE = 'STRIPE',
}

export type PgPaymentMethodType = (typeof PgPaymentMethod)[keyof typeof PgPaymentMethod];

export const PAYMENT_METHODS = {
  CARD: '카드',
  PHONE: '휴대폰',
  GIFT_CARD: '문화상품권',
} as const;

export type PaymentMethodsName = (typeof PAYMENT_METHODS)[keyof typeof PAYMENT_METHODS];

export interface OrderInfo {
  card?: {
    company: string;
    number: string;
    installmentPlanMonths: number;
  };
  mobilePhone?: {
    carrier: string;
    customerMobilePhone: string;
  };
  method: string;
  orderId: string;
  totalAmount: string;
  product: GemProduct;
}

export interface GemProduct {
  productId: string;
  productName: string;
  baseGemAmount: number;
  bonusGemAmount: number;
  promoGemAmount: number;
  membershipGemAmount: number;
  price: number;
  currency: string;
  iconUrl: string;
  backgroundColor: string;
}

export type PurchasableProduct =
  | GemProduct
  | ProductModule
  | DTOGemProductModule
  | DTOVividiOneTimePlanModule
  | DTOItemProductModule;

export const isPurchasableProductModule = (
  purchasableProduct: PurchasableProduct
): purchasableProduct is ProductModule => {
  const requiredKeys = ['productType', 'productId', 'productName', 'pgPrice'];

  return requiredKeys.every((requiredKey) => requiredKey in purchasableProduct);
};

/**
 * 이벤트 로그를 위해서만 활용하는 결제 팝업 관련 메타 데이터
 * (주의) 해당 값은 세션 스토리지를 통해 유지되어 변조 가능하므로 그 외의 용도로는 사용 불가능
 */
export interface PaymentPopupInfo {
  origins: ShopOrigin[];
  /**
   * 결제창을 연 시점의 탭 정보
   */
  originTab: string;

  product: PurchasableProduct;
}

export const PG_ERROR_CODE = {
  PAY_PROCESS_CANCELED: 'PAY_PROCESS_CANCELED',
  PAY_PROCESS_ABORTED: 'PAY_PROCESS_ABORTED',
  REJECT_CARD_COMPANY: 'PAY_REJECT_CARD_COMPANY',
  NOT_FOUND_PRODUCT: 'API_NOT_FOUND_PRODUCT',
  INVALID_AMOUNT: 'API_INVALID_AMOUNT',
  INVALID_RESERVED_USER: 'API_INVALID_RESERVATION',
  NOT_FOUND_RESERVED_PAYMENT: 'API_INVALID_RESERVATION',
  FAILED_APPROVE_PAYMENT: 'API_FAILED_APPROVE_PAYMENT',
  INVALID_BILLING: 'INVALID_BILLING',
  TIME_OUT: 'TIME_OUT',
  ERROR: 'ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  IMPLEMENTATION_ERROR: 'IMPLEMENTATION_ERROR',
  COMMON_ERROR: 'COMMON_ERROR',
} as const;

type ResponseCode = keyof typeof PG_ERROR_CODE;

export const getErrorCode = (code?: string) => {
  if (!code) return PG_ERROR_CODE.ERROR;

  return PG_ERROR_CODE[code as ResponseCode] || PG_ERROR_CODE.ERROR;
};

export const tossTestClientKey = process.env.NEXT_PUBLIC_TOSS_CLIENT_KEY || '';

export const adyenClientKey = process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY || '';

export const paymentsEnvironment = process.env.NEXT_PUBLIC_ENVIRONMENT === 'Prod' ? 'live' : 'test';

export interface PaymentsCompleteRequest {
  orderId: string;
  /**
   * 토스 결제 관련해서만 필요
   */
  paymentKey?: string;
}

export interface PaymentsCompleteResponse {
  orderId: string;
  product: {
    baseGemAmount: number;
    bonusGemAmount: number;
    isGemProduct: boolean;
    productName: string;
    promoGemAmount: number;
  };
  /**
   * 상품의 가격 (구매한 유저의 화폐 기준)
   */
  amount: number;

  currency: string;
  paymentMethod: string;
}

export interface BillingInfoRequest {
  productId: string;
  price: number;
  currency: string;
}

export interface BillingInfoResponse {
  paymentMethods: {
    method: PgPaymentMethodType;
    bonusGemAmount?: number;
    bonusGemRates?: BonusGemRate[];
  }[];
  product: {
    baseGemAmount: number;
    promoGemAmount: number;
  };
}

export interface PaymentsReserveRequest {
  paymentMethod: string;
  productId: string;
  productName: string;
  amount: string;
  currency: string;
}

export interface TossPaymentsReserveResponse {
  orderId: string;
}

export interface AdyenPaymentsReserveResponse extends TossPaymentsReserveResponse {
  sessionId: string;
  sessionData: string;
}

export interface StripePaymentsReserveResponse extends TossPaymentsReserveResponse {
  url: string; // 결제창 session url
}

export type PaymentsReserveResponse =
  | TossPaymentsReserveResponse
  | AdyenPaymentsReserveResponse
  | StripePaymentsReserveResponse;

interface PaymentMethodToResponseMap {
  [PgPaymentMethod.TOSS]: TossPaymentsReserveResponse;
  [PgPaymentMethod.ADYEN]: AdyenPaymentsReserveResponse;
  [PgPaymentMethod.STRIPE]: StripePaymentsReserveResponse;
}

export type PaymentsReservation = {
  [K in keyof PaymentMethodToResponseMap]: PaymentMethodToResponseMap[K] & {
    paymentMethod: K;
  };
}[keyof PaymentMethodToResponseMap];

export interface GetGemProductsRequest {
  platform: NativePlatform | Platform.WebClient;
}

export interface GemProductsResponse {
  paymentMethod: PgPaymentMethodType;
  gemProducts: GemProduct[];
  bonusGemRates?: BonusGemRate[];
}

export interface BonusGemRate {
  type: 'BONUS_COUPON' | 'VIP_BONUS' | 'PG_BONUS';
  rate: number;
  vipRating?: string;
}

export const SUBSCRIPTION_SECTION_TYPES = {
  HEADERS: 'SUB_HEADERS',
  BENEFITS: 'SUB_BENEFITS',
  AUTO_RENEW_PLANS: 'VIVIDI_AUTO_RENEW_PLANS',
  ONE_TIME_PLANS: 'VIVIDI_ONE_TIME_PLANS',
  TERMS: 'SUB_TERMS',
} as const;

export type SubscriptionSectionType =
  (typeof SUBSCRIPTION_SECTION_TYPES)[keyof typeof SUBSCRIPTION_SECTION_TYPES];

export const SHOP_SECTION_TYPES = {
  GEM_PRODUCTS: 'GEM_PRODUCTS',
  SUB_PLANS: 'SUB_PLANS',
  VIP_BANNER: 'VIP_BANNER',
} as const;

export type ShopSectionType = (typeof SHOP_SECTION_TYPES)[keyof typeof SHOP_SECTION_TYPES];
